// Build ONE quote slide and prepend it to a tour. For a non-technical user (the
// docent's mom): pick a tour (required), give the slide a title, type/paste one or
// more quotes, watch a live iPad-Pro-13" preview whose font shrinks to fit, then
// Submit — the slide is prepended to the chosen tour (its id leads `order`).
//
// The preview reports the fitted body font-size (px @ a 1032-wide canvas); we store
// it on the slide as `fontPx` so the JS-free offline export reproduces the same fit.
// Quotes follow "Collection Up Close" styling (Georgia serif on black); the sizing is
// the only dynamic part. Row editing + bulk paste mirror the old QuotesPanel.

import { useMemo, useState } from "react";
import type { Quote, QuoteSlide } from "../types";
import { tourTitle, type TourGroup } from "../tours";
import { parseQuotesBlob } from "../quotes";
import { QuoteSlidePreview } from "../components/QuoteSlidePreview";
import "./QuoteSlideBuilder.css";

interface QuoteSlideBuilderProps {
  /** Tours to choose from (the selector). */
  groups: TourGroup[];
  /** Preselected tour slug (e.g. opened from a tour's detail view). */
  initialSlug?: string;
  /** Add + prepend the slide to a tour (durable: PUT /tours/{slug} {quoteSlides, order}). */
  addQuoteSlide: (slug: string, slide: QuoteSlide) => Promise<void>;
  /** Leave the builder. If `slug` is given, open that tour to show the new slide on top. */
  onDone: (slug?: string) => void;
}

/** A working quote row carries a stable key so React keeps focus across reorders. */
interface Row {
  key: string;
  attribution: string;
  text: string;
}

let rowSeq = 0;
function nextKey(): string {
  rowSeq += 1;
  return `qsb${rowSeq}`;
}

function rowsFromQuotes(quotes: Quote[]): Row[] {
  return quotes.map((q) => ({
    key: nextKey(),
    attribution: q.attribution ?? "",
    text: q.text,
  }));
}

/** Project rows to the `Quote[]` we persist: trim, drop empty-text rows. */
function rowsToQuotes(rows: Row[]): Quote[] {
  const out: Quote[] = [];
  for (const r of rows) {
    const text = r.text.trim();
    if (!text) continue;
    const attribution = r.attribution.trim();
    out.push(attribution ? { attribution, text } : { text });
  }
  return out;
}

/** Mint a unique quote-slide id ("qs_"+uuid), with a fallback for old WebKit. */
function mkId(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return `qs_${c.randomUUID()}`;
  return `qs_${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

export function QuoteSlideBuilder({
  groups,
  initialSlug,
  addQuoteSlide,
  onDone,
}: QuoteSlideBuilderProps) {
  const [slug, setSlug] = useState(initialSlug ?? "");
  const [title, setTitle] = useState("");
  const [rows, setRows] = useState<Row[]>(() => [
    { key: nextKey(), attribution: "", text: "" },
  ]);
  const [paste, setPaste] = useState("");
  // Fitted body font-size from the live preview (px @ a 1032-wide canvas).
  const [fontPx, setFontPx] = useState<number | undefined>(undefined);
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const quotes = useMemo(() => rowsToQuotes(rows), [rows]);
  const saving = status === "saving";
  const canSubmit = slug !== "" && quotes.length > 0 && !saving;

  // --- Row editing -----------------------------------------------------------
  const clearStatus = () => {
    if (status !== "idle") {
      setStatus("idle");
      setErrorMsg(null);
    }
  };

  const updateRow = (key: string, patch: Partial<Pick<Row, "attribution" | "text">>) => {
    clearStatus();
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const removeRow = (key: string) => {
    clearStatus();
    setRows((rs) => rs.filter((r) => r.key !== key));
  };

  const moveRow = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    clearStatus();
    setRows((rs) => {
      const next = [...rs];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved);
      return next;
    });
  };

  const addBlankRow = () => {
    clearStatus();
    setRows((rs) => [...rs, { key: nextKey(), attribution: "", text: "" }]);
  };

  const addPasted = () => {
    const parsed = parseQuotesBlob(paste);
    if (parsed.length === 0) return;
    clearStatus();
    setRows((rs) => {
      // Drop a single leading blank row so paste-into-empty feels natural.
      const base = rs.length === 1 && !rs[0].attribution && !rs[0].text ? [] : rs;
      return [...base, ...rowsFromQuotes(parsed)];
    });
    setPaste("");
  };

  // --- Submit ----------------------------------------------------------------
  const handleSubmit = async () => {
    if (!canSubmit) return;
    setStatus("saving");
    setErrorMsg(null);
    const slide: QuoteSlide = { id: mkId(), quotes };
    const t = title.trim();
    if (t) slide.title = t;
    if (fontPx) slide.fontPx = fontPx;
    try {
      await addQuoteSlide(slug, slide);
      setStatus("saved");
      onDone(slug); // open the tour so she sees the slide prepended on top
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Couldn’t add the slide — try again.");
      setStatus("error");
    }
  };

  const noTours = groups.length === 0;

  return (
    <div className="qsb">
      <header className="qsb__header">
        <button type="button" className="btn btn--small" onClick={() => onDone()}>
          ← Back
        </button>
        <h1 className="qsb__title">New quote slide</h1>
      </header>

      {noTours ? (
        <div className="empty-state">
          <p>No tours yet.</p>
          <p>
            Tag some artworks with a tour name in the ingest app first — then you can
            add a quote slide to that tour here.
          </p>
        </div>
      ) : (
        <div className="qsb__layout">
          {/* ---------- Editor ---------- */}
          <div className="qsb__editor">
            <label className="qsb__field">
              <span className="qsb__label">
                Tour <span className="qsb__req">(required)</span>
              </span>
              <select
                className="qsb__select"
                value={slug}
                onChange={(e) => {
                  clearStatus();
                  setSlug(e.target.value);
                }}
                disabled={saving}
              >
                <option value="" disabled>
                  — Choose a tour —
                </option>
                {groups.map((g) => (
                  <option key={g.slug} value={g.slug}>
                    {tourTitle(g.slug)}
                  </option>
                ))}
              </select>
            </label>

            <label className="qsb__field">
              <span className="qsb__label">Slide title (optional)</span>
              <input
                type="text"
                className="qsb__input"
                placeholder="e.g. In Their Own Words"
                value={title}
                onChange={(e) => {
                  clearStatus();
                  setTitle(e.target.value);
                }}
                autoCapitalize="words"
                disabled={saving}
              />
            </label>

            <div className="qsb__quotes">
              <span className="qsb__label">Quotes</span>
              {rows.length === 0 ? (
                <p className="qsb__empty">No quotes yet — add one or paste several below.</p>
              ) : (
                <ul className="qsb__list">
                  {rows.map((row, i) => (
                    <li key={row.key} className="qsb__row">
                      <div className="qsb__rowfields">
                        <input
                          type="text"
                          className="qsb__attr"
                          placeholder="Who said it (optional)"
                          value={row.attribution}
                          onChange={(e) => updateRow(row.key, { attribution: e.target.value })}
                          autoCapitalize="words"
                          spellCheck={false}
                          disabled={saving}
                        />
                        <textarea
                          className="qsb__text"
                          placeholder="The quote…"
                          rows={2}
                          value={row.text}
                          onChange={(e) => updateRow(row.key, { text: e.target.value })}
                          disabled={saving}
                        />
                      </div>
                      <div className="qsb__rowctl">
                        <button
                          type="button"
                          className="btn btn--small qsb__move"
                          onClick={() => moveRow(i, -1)}
                          disabled={i === 0 || saving}
                          aria-label="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="btn btn--small qsb__move"
                          onClick={() => moveRow(i, 1)}
                          disabled={i === rows.length - 1 || saving}
                          aria-label="Move down"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="btn btn--small btn--danger"
                          onClick={() => removeRow(row.key)}
                          disabled={saving}
                          aria-label="Remove quote"
                        >
                          ✕
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="qsb__rowadd">
                <button type="button" className="btn btn--small" onClick={addBlankRow} disabled={saving}>
                  + Add a quote
                </button>
              </div>

              <label className="qsb__field">
                <span className="qsb__label">Paste several quotes</span>
                <textarea
                  className="qsb__pastebox"
                  placeholder={
                    "Lorenzo de Medici\nA “citizen of authority”\n\nRembrandt\n“Life etches itself onto our faces…”"
                  }
                  rows={4}
                  value={paste}
                  onChange={(e) => setPaste(e.target.value)}
                  disabled={saving}
                />
              </label>
              <p className="qsb__hint">
                One quote per block, blocks separated by a blank line. The first line of a
                block is who said it; the rest is the quote.
              </p>
              <button
                type="button"
                className="btn btn--small"
                onClick={addPasted}
                disabled={saving || paste.trim() === ""}
              >
                Add pasted quotes
              </button>
            </div>

            {/* Submit bar */}
            <div className="qsb__savebar">
              <button
                type="button"
                className="btn btn--primary qsb__submit"
                onClick={() => void handleSubmit()}
                disabled={!canSubmit}
                aria-busy={saving}
              >
                {saving ? "Adding…" : "Add to top of tour"}
              </button>
              <span
                className={`qsb__status${status === "error" ? " qsb__status--error" : ""}`}
                role={status === "error" ? "alert" : undefined}
                aria-live="polite"
              >
                {status === "error"
                  ? `Couldn’t add: ${errorMsg ?? "please try again."}`
                  : slug === ""
                    ? "Choose a tour to continue."
                    : quotes.length === 0
                      ? "Add at least one quote."
                      : `Prepends 1 slide (${quotes.length} ${
                          quotes.length === 1 ? "quote" : "quotes"
                        }) to “${tourTitle(slug)}”.`}
              </span>
            </div>
          </div>

          {/* ---------- Live iPad preview ---------- */}
          <div className="qsb__preview">
            <p className="qsb__preview-label">Preview · iPad Pro 13″</p>
            <QuoteSlidePreview title={title} quotes={quotes} onFit={setFontPx} />
          </div>
        </div>
      )}
    </div>
  );
}
